<template>
    <ListFrame
        class="documentation-list-frame"
        :class="{ 'refetch-loading': refetchLoading }"
        :loading="initialDocsLoading"
        :error="error"
        :no-results="!resultExists"
        :total="total"
    >
        <template #filter="{ isMobile }">
            <ListFilterUrlSync
                v-model:selected="selected"
                :facet-collection-id="FacetCollection.documentSelection"
                :is-mobile-view="isMobile"
                :fetch-aggregation="fetchAggregation"
                :product="productID"
                :more-menu-entries="$q.screen.gt.xs ? [] : menuEntries"
                :sort-order-entries="sortOrderItems"
                :show-grouping="showGrouping"
                grouping-save-key="documentation_grouping"
                sort-save-key="documentation_sort"
                @on-sort-selection-changed="onChangeSortOrder"
                @on-open-filter="onOpenFilterHandler"
            />
        </template>
        <template #banner v-if="showBannerTop">
            <SearchBanner
                :search-result-status="resultExists ? 'tooMany' : 'none'"
                :context="searchContext"
                @on-open-need-qualification="$emit('onOpenNeedQualification')"
            />
        </template>
        <template #content>
            <!-- The loading spinner in the ListFrame is not compatible with the InfinityScroll
            therefore the workaround is necessary with refetchloading and own loading spinner in the template content -->
            <div v-if="refetchLoading">
                <div class="loading-container small-content-container" data-qs="loading-fragment-spinner">
                    <q-spinner-ios size="2em" />
                </div>
            </div>
            <InfinityScroll @load-more="onLoadMore" :reset-key="infiniteResetKey">
                <div v-if="isGroupingDisableHintEnabled">
                    <Banner class="q-mb-md">
                        <template #icon>
                            <q-icon name="fa-regular fa-circle-info" color="warning" size="40px" />
                        </template>
                        <template #content>
                            {{ $t("documentation.Search results cannot be displayed in groups") }}
                        </template>
                    </Banner>
                </div>
                <div class="column gap">
                    <template v-if="isGroupingEnabled">
                        <DocumentGroupedListCard
                            v-for="(group, index) in groupedContents"
                            :key="group.cursor"
                            :group-result="group"
                            :context="searchContext"
                            :index="index"
                            :items-reset-key="groupItemsResetKey"
                            @toggle-collection-overlay="onOpenOverlay"
                            @load-more-items="onLoadMoreGroupItems"
                        />
                    </template>
                    <template v-else>
                        <DocumentListCard
                            v-for="(item, index) in contents"
                            :key="item.id"
                            :document="item"
                            :context="searchContext"
                            :index="index"
                            @toggle-collection-overlay="onOpenOverlay"
                        />
                    </template>
                </div>
            </InfinityScroll>
            <div v-if="tooFewResults">
                <SearchBanner
                    class="q-my-md"
                    search-result-status="tooFew"
                    :context="searchContext"
                    @on-open-need-qualification="$emit('onOpenNeedQualification')"
                />
            </div>
        </template>
        <template #no-results>
            <NoSearchResultsError
                :search-tab="$t('documentation.Documentation')"
                :class="{ 'q-mx-lg': $q.screen.lt.sm }"
            />
        </template>
    </ListFrame>
    <CollectionContentOverlay
        v-if="contentMapIdForOverlay"
        :title="$t('documentation.Add to Collection')"
        :content-map-id="contentMapIdForOverlay"
        :topic-id="topicIdForOverlay"
        :product-id="product"
        :asset-id="asset"
        @on-close="() => closeOverlay()"
        @on-save-succeeded="() => closeOverlay()"
    />
</template>

<script setup lang="ts">
import { computed, ref, toRefs, watch } from "vue";
import { provideGroupingToggleDisabled } from "@/shared/list-filter/composables/grouping-toggle-disabled";
import CollectionContentOverlay from "@/abilities/collections/components/overlay/collection-content-overlay.vue"; //TODO Remove dependency to collection module
import ListFilterUrlSync from "@/shared/list-filter/list-filter-renderer-url-sync.vue";
import { Facet, FacetCollection, FacetSelected } from "@/shared/facets/facets.model";
import ListFrame from "@/shared/list-frame/list-frame.vue";
import { useSelectedFacets } from "@/shared/facets/composables/selected-facets";
import { useFilteredDocumentationResults } from "./filtered-documentation-results.ts";
import { useSortOrderItems } from "./documentation-sort-order-items";
import DocumentListCard from "@/shared/documentation/document-list-card.vue";
import DocumentGroupedListCard from "@/shared/documentation/document-grouped-list-card.vue";
import InfinityScroll from "@/shared/components/scroll/infinity-scroll.vue";
import { useHeaderMenuEntries } from "@/shared/search/header-menu-entries.ts";
import { CommonSearchTabAliases } from "@/shared/environment/common-route-aliases.ts";
import { SearchContext } from "@/shared/search/search.types.ts";
import SearchBanner from "@/shared/search/search-banner.vue";
import { TOO_FEW_RESULTS_THRESHOLD, TOO_MANY_RESULTS_THRESHOLD } from "@/shared/search/search.constants.ts";
import NoSearchResultsError from "@/shared/search/no-search-results-error.vue";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import { watchDeep } from "@vueuse/core";
import { LAZY_LOAD_AMOUNT } from "@/shared/environment/consts.ts";
import { useMatomo } from "@/shared/analytics/matomo.ts";
import { useContentGroupingConfigQuery } from "@/shared/services/graphql/generated/consumer-graph-types.ts";
import Banner from "@/shared/banner/banner.vue";
import { useGrouping } from "@/shared/grouping/composable/grouping.ts";

const props = defineProps<{
    text?: string;
    product?: string;
    asset?: string;
    mechanic?: string;
    mechanicArticle?: string;
    isSearch?: boolean;
}>();

defineEmits<{ onOpenNeedQualification: [] }>();

const { product, asset, text, mechanic, mechanicArticle } = toRefs(props);
const contentMapIdForOverlay = ref<string | undefined>();
const topicIdForOverlay = ref<string>("");
const selected = ref<FacetSelected[]>([]);
const facet = ref<Facet>();
const resultExists = ref(false);
const isOnMoreLoading = ref(false);
const infiniteResetKey = ref<number>(0);

const { isGrouped: groupingToggle } = useGrouping("documentation_grouping");

const { result: contentGroupingConfig } = useContentGroupingConfigQuery({ id: "documentation" });

const showGrouping = computed(() => {
    return contentGroupingConfig.value?.contentGroupingConfig?.enabled ?? false;
});

// the saved preference only counts while the admin has grouping enabled - otherwise the list is plain
const isGroupingSwitchedOn = computed(() => showGrouping.value && groupingToggle.value);

const { sortOrderItems } = useSortOrderItems(isGroupingSwitchedOn);
const { sorting, onChangeSortOrder } = useSelectedFacets();
const { menuEntries } = useHeaderMenuEntries(computed(() => CommonSearchTabAliases.documentations));
const { hasAccess: hasProducts } = useHasAccess({ featureID: AccessFeature.products });
const { trackSearch } = useMatomo();

const productID = computed(() => {
    if (!asset?.value && product?.value) return product?.value;

    return undefined;
});

/*
A changed sorting invalidates the items already loaded per group, but leaves the group list untouched - the
cards are reused. Kept apart from `infiniteResetKey` so that the group list itself is not reset
for a sorting that does not affect it.
*/
const groupItemsResetKey = ref<number>(0);

/*
A changed filter or grouping replaces the group list and drops the loaded items as well. Group cards are
keyed by their cursor, so a card can be reused - its infinite scroll has to start over too, otherwise an
expanded group that had already loaded all its items would stay empty.
*/
watchDeep([selected, isGroupingSwitchedOn], () => {
    infiniteResetKey.value += 1;
    groupItemsResetKey.value += 1;
});

watch(sorting, () => {
    groupItemsResetKey.value += 1;
});

const onOpenOverlay = (topicId: string, contentMapId: string) => {
    contentMapIdForOverlay.value = contentMapId;
    topicIdForOverlay.value = topicId;
};

const closeOverlay = () => {
    contentMapIdForOverlay.value = undefined;
};

const searchContext = computed<SearchContext>(() => {
    return {
        product: product.value,
        asset: asset.value,
        mechanic: mechanicArticle.value,
        mechanicPath: mechanic.value,
    };
});

const searchPhrase = computed(() => {
    return text?.value ?? "";
});

const groupingCriteria = computed(() => {
    if (!isGroupingSwitchedOn.value) return null;
    return contentGroupingConfig.value?.contentGroupingConfig?.criteria;
});

const isGroupingToggleDisabledBySearch = computed(() => searchPhrase.value.length > 0);
provideGroupingToggleDisabled(isGroupingToggleDisabledBySearch);

const {
    contents,
    groupedContents,
    total,
    error,
    loadMore,
    loadMoreGroupItems,
    fetchAggregation,
    loading,
    isGroupingEnabled,
} = useFilteredDocumentationResults({
    selected,
    facet,
    searchContext,
    searchPhrase,
    sort: sorting,
    groupingCriteria,
});

const isGroupingDisableHintEnabled = computed(() => isGroupingSwitchedOn.value && searchPhrase.value.length > 0);

const activeResults = computed(() => (isGroupingEnabled.value ? groupedContents.value : contents.value));

watch(activeResults, () => {
    resultExists.value = total.value?.count ? total.value.count > 0 : false;
    trackSearch(searchPhrase.value, "documentation", total.value?.count ?? 0);
});

//an errored query never produces results - without this the endless spinner would hide the error message
const initialDocsLoading = computed(() => {
    return !activeResults.value && !error.value;
});

const refetchLoading = computed(() => {
    return loading.value && !isOnMoreLoading.value;
});

const onLoadMore = async (done: (stop: boolean) => void) => {
    isOnMoreLoading.value = true;

    //while grouping is active the group list is paginated, not the contents
    const items = isGroupingEnabled.value ? groupedContents.value : contents.value;

    if (!items || items.length < 1 || items.length >= (total.value?.count ?? 0)) {
        isOnMoreLoading.value = false;
        done(true);
        return;
    }

    const lastCursor = items[items.length - 1].cursor ?? "";
    try {
        const loadMoreLastCursor = await loadMore(lastCursor, LAZY_LOAD_AMOUNT);

        //stop when nothing was loaded or the same page came back again, otherwise we would loop endlessly
        done(loadMoreLastCursor ? lastCursor === loadMoreLastCursor : true);
    } catch (e) {
        console.error(e);
        done(true);
    } finally {
        isOnMoreLoading.value = false;
    }
};

const onLoadMoreGroupItems = async (groupCursor: string, done: (stop?: boolean) => void) => {
    try {
        const lastItemCursor = await loadMoreGroupItems(groupCursor, LAZY_LOAD_AMOUNT);

        //stop when nothing was loaded, otherwise the infinite scroll would ask for the same page again
        done(!lastItemCursor);
    } catch (e) {
        console.error(e);
        done(true);
    }
};

const onOpenFilterHandler = (facetProp: Facet) => {
    facet.value = facetProp;
};

const isBannerAllowed = computed(() => {
    if (!hasProducts.value) return false;
    return !(props.product || !props.isSearch || (loading.value && !isOnMoreLoading.value));
});

const showBannerTop = computed(() => {
    if (!isBannerAllowed.value) return false;

    return (total.value && total.value.count > TOO_MANY_RESULTS_THRESHOLD) || !resultExists.value;
});

const tooFewResults = computed(() => {
    if (!isBannerAllowed.value) return false;

    return total.value && total.value.count <= TOO_FEW_RESULTS_THRESHOLD;
});
</script>

<style lang="scss" scoped>
:deep(.scroll-container) {
    max-width: $small-max-width;
}
.documentation-list-frame {
    :deep(.slot-content) {
        display: grid;
        grid-template-rows: 1fr auto;
    }
}

.loading-container {
    z-index: 1;
    position: absolute;
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
    margin-top: 200px;
}
.refetch-loading {
    .q-scrollarea {
        opacity: 0.6;
    }
}
</style>
